import { useState, useEffect } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { setClientIconUrl } from '../../../utils/clientIcon';
import { isSplashFileAllowed } from '../../../utils/splashMedia';
import { resolveMediaSrc } from '../../../utils/resolveMediaSrc';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// The server returns two forms of the same media: the RAW stored value
// (`gifUrl`/`iconUrl` — either an admin-typed external URL, or one of our
// own GCS/local storage keys) and a RESOLVED display value (`fullUrl`/
// `iconFullUrl` — a signed URL or `local://...` sentinel, good for exactly
// one render and never valid to store back). The "external URL" text input
// must only ever be populated from the raw value — and only when that raw
// value is itself a genuine external URL — otherwise saving the form again
// (e.g. just to toggle Active, or to change the other field) resubmits the
// resolved value as if it were permanent storage and corrupts it (this is
// exactly how a real record ended up with iconUrl stored as
// "local://local://client-icons/....png").
const externalUrlForInput = (raw) => (/^https?:\/\//i.test(raw || '') ? raw : '');

const MAX_ICON_BYTES = 5 * 1024 * 1024;
const ICON_MIME_TYPES = [
  'image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp',
  'image/x-icon', 'image/vnd.microsoft.icon',
];
const ICON_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.svg', '.webp', '.ico'];

// Browsers often report .ico files with an empty or non-standard type, so
// fall back to the extension (the server does the same).
const isIconFileAllowed = (file) => {
  if (ICON_MIME_TYPES.includes(file.type)) return true;
  const name = (file.name || '').toLowerCase();
  return ICON_EXTENSIONS.some((ext) => name.endsWith(ext));
};

export const useSplash = (showMessage) => {
  const [splashInfo, setSplashInfo] = useState(null);
  const [loadingSplash, setLoadingSplash] = useState(false);
  const [uploadingSplash, setUploadingSplash] = useState(false);
  const [splashUrlInput, setSplashUrlInput] = useState('');
  const [splashFile, setSplashFile] = useState(null);
  const [splashPreviewUrl, setSplashPreviewUrl] = useState(null);
  const [splashActive, setSplashActive] = useState(true);
  const [iconUrlInput, setIconUrlInput] = useState('');
  const [iconFile, setIconFile] = useState(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);
  // Set by "Remove icon": the next save clears the stored icon, even an
  // uploaded one (whose key never appears in the URL input to be blanked).
  const [iconRemoved, setIconRemoved] = useState(false);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(splashPreviewUrl);
      }
    };
  }, [splashPreviewUrl]);

  useEffect(() => {
    return () => {
      if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(iconPreviewUrl);
      }
    };
  }, [iconPreviewUrl]);

  const resetSplashState = () => {
    if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(splashPreviewUrl);
    }
    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setSplashInfo(null);
    setSplashUrlInput('');
    setSplashFile(null);
    setSplashPreviewUrl(null);
    setSplashActive(true);
    setIconUrlInput('');
    setIconFile(null);
    setIconPreviewUrl(null);
    setIconRemoved(false);
  };

  // Applies a freshly-fetched/saved record to all the derived state in one
  // place, so the raw-vs-resolved split above can't drift between the three
  // call sites (fetch, save, deactivate) that used to each do this inline.
  const applyRecord = (record) => {
    setSplashInfo(record);
    setSplashActive(record?.isActive !== false);

    const previewGifUrl = resolveMediaSrc(record?.fullUrl || record?.gifUrl || '');
    setSplashUrlInput(externalUrlForInput(record?.gifUrl));
    setSplashPreviewUrl(previewGifUrl || null);
    setSplashFile(null);

    const previewIconUrl = resolveMediaSrc(record?.iconFullUrl || record?.iconUrl || '');
    setIconUrlInput(externalUrlForInput(record?.iconUrl));
    setIconPreviewUrl(previewIconUrl || null);
    setIconFile(null);
    setIconRemoved(false);

    return previewIconUrl;
  };

  const fetchSplashData = async (clientName) => {
    if (!clientName) {
      resetSplashState();
      return;
    }

    try {
      setLoadingSplash(true);
      const response = await apiFetch(
        `${BASE_URL}/api/client-splash/${encodeURIComponent(clientName)}`,
        { method: 'GET' }
      );

      if (response.ok) {
        const json = await response.json();
        applyRecord(json?.data || null);
      } else if (response.status === 404) {
        resetSplashState();
      } else {
        throw new Error('Unable to load splash screen details');
      }
    } catch (error) {
      console.error('Failed to fetch splash screen:', error);
      showMessage('error', 'Failed to load splash screen');
    } finally {
      setLoadingSplash(false);
      setSplashFile(null);
      setIconFile(null);
    }
  };

  const handleSplashFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    if (!isSplashFileAllowed(file)) {
      showMessage('error', 'Only GIF, MP4, WEBM, MOV, or OGG files are allowed');
      event.target.value = '';
      return;
    }

    if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(splashPreviewUrl);
    }

    const preview = URL.createObjectURL(file);
    setSplashFile(file);
    setSplashPreviewUrl(preview);
    setSplashUrlInput('');
  };

  const clearSplashFile = () => {
    if (splashPreviewUrl && splashPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(splashPreviewUrl);
    }
    setSplashFile(null);
    setSplashPreviewUrl(resolveMediaSrc(splashInfo?.fullUrl || splashInfo?.gifUrl || '') || null);
    setSplashUrlInput(externalUrlForInput(splashInfo?.gifUrl));
  };

  const handleIconFileChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (!isIconFileAllowed(file)) {
      showMessage('error', 'Icon must be PNG, JPG, JPEG, SVG, WEBP, or ICO');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_ICON_BYTES) {
      showMessage('error', 'Icon must be 5MB or smaller');
      event.target.value = '';
      return;
    }

    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }

    const preview = URL.createObjectURL(file);
    setIconFile(file);
    setIconPreviewUrl(preview);
    setIconUrlInput('');
    setIconRemoved(false);
  };

  const clearIconFile = () => {
    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setIconFile(null);
    setIconPreviewUrl(resolveMediaSrc(splashInfo?.iconFullUrl || splashInfo?.iconUrl || '') || null);
    setIconUrlInput(externalUrlForInput(splashInfo?.iconUrl));
    setIconRemoved(false);
  };

  const removeIcon = () => {
    if (iconPreviewUrl && iconPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setIconFile(null);
    setIconUrlInput('');
    setIconPreviewUrl(null);
    setIconRemoved(true);
  };

  const handleSplashSave = async (selectedCompany) => {
    if (!selectedCompany) {
      showMessage('error', 'Select a company first');
      return;
    }

    const trimmedGifUrl = splashUrlInput.trim();
    const trimmedIconUrl = iconUrlInput.trim();

    // Nothing typed → resend the RAW stored value verbatim (a storage key,
    // or the original external URL) instead of leaving it blank, so a save
    // that only touches the other field (or just flips Active/Inactive)
    // can't wipe or corrupt it. The one case a blank input SHOULD mean
    // "clear" is when the raw value was itself an external URL that the
    // admin visibly deleted from the field — anything else (file-based key,
    // or never set) has nothing shown to "clear" in the first place.
    const gifRawWasExternal = !!externalUrlForInput(splashInfo?.gifUrl);
    const gifUrlToSend = trimmedGifUrl || (gifRawWasExternal ? '' : (splashInfo?.gifUrl || ''));

    const iconRawWasExternal = !!externalUrlForInput(splashInfo?.iconUrl);
    const iconUrlToSend =
      trimmedIconUrl || (iconRemoved || iconRawWasExternal ? '' : (splashInfo?.iconUrl || ''));

    // Either is enough — a company can have only a badge icon.
    if (!splashFile && !gifUrlToSend && !iconFile && !iconUrlToSend) {
      showMessage('error', 'Add splash media or an icon');
      return;
    }

    try {
      setUploadingSplash(true);
      let response;
      const shouldUseFormData = Boolean(splashFile || iconFile);

      if (shouldUseFormData) {
        const formData = new FormData();
        formData.append('clientName', selectedCompany);
        formData.append('isActive', splashActive ? 'true' : 'false');
        if (splashFile) {
          formData.append('splashGif', splashFile);
        } else {
          formData.append('gifUrl', gifUrlToSend);
        }
        if (iconFile) {
          formData.append('splashIcon', iconFile);
        } else {
          formData.append('iconUrl', iconUrlToSend);
        }

        response = await apiFetch(`${BASE_URL}/api/client-splash`, {
          method: 'POST',
          body: formData,
        });
      } else {
        response = await apiFetch(`${BASE_URL}/api/client-splash`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clientName: selectedCompany,
            gifUrl: gifUrlToSend,
            iconUrl: iconUrlToSend,
            isActive: splashActive,
          }),
        });
      }

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to save splash screen');
      }

      const json = await response.json();
      const record = json?.data || null;
      const iconValue = applyRecord(record);

      const loggedClientName = localStorage.getItem('clientName');
      if (
        loggedClientName &&
        selectedCompany &&
        loggedClientName.trim().toLowerCase() === selectedCompany.trim().toLowerCase()
      ) {
        setClientIconUrl(iconValue || null);
      }

      showMessage('success', 'Splash screen saved successfully');
    } catch (error) {
      console.error('Failed to save splash screen:', error);
      showMessage('error', error.message || 'Failed to save splash screen');
    } finally {
      setUploadingSplash(false);
    }
  };

  const handleSplashDeactivate = async (selectedCompany) => {
    if (!selectedCompany) {
      showMessage('error', 'Select a company first');
      return;
    }

    try {
      setUploadingSplash(true);
      const response = await apiFetch(
        `${BASE_URL}/api/client-splash/${encodeURIComponent(selectedCompany)}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || 'Failed to deactivate splash screen');
      }

      const json = await response.json();
      const record = json?.data || null;
      const iconValue = applyRecord(record);

      const loggedClientName = localStorage.getItem('clientName');
      if (
        loggedClientName &&
        selectedCompany &&
        loggedClientName.trim().toLowerCase() === selectedCompany.trim().toLowerCase()
      ) {
        setClientIconUrl(iconValue || null);
      }

      showMessage('success', 'Splash screen deactivated');
    } catch (error) {
      console.error('Failed to deactivate splash screen:', error);
      showMessage('error', error.message || 'Failed to deactivate splash screen');
    } finally {
      setUploadingSplash(false);
    }
  };

  const toggleSplashActive = () => {
    setSplashActive((prev) => !prev);
  };

  return {
    splashInfo,
    loadingSplash,
    uploadingSplash,
    splashUrlInput,
    setSplashUrlInput,
    splashFile,
    splashPreviewUrl,
    splashActive,
    iconUrlInput,
    setIconUrlInput,
    iconFile,
    iconPreviewUrl,
    setIconPreviewUrl,
    iconRemoved,
    removeIcon,
    resetSplashState,
    fetchSplashData,
    handleSplashFileChange,
    clearSplashFile,
    handleIconFileChange,
    clearIconFile,
    handleSplashSave,
    handleSplashDeactivate,
    toggleSplashActive,
  };
};
